import { useState } from "react";
import { ArrowRight } from "lucide-react";

import type { FinanceReadinessPreviewItem } from "../../../lib/dashboard";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Dialog, DialogContent } from "../../ui/dialog";
import { TierBadge } from "../../ui/tier-badge";
import {
  DataSourceChooser,
  type DataSourceOption,
} from "./data-source-chooser";

export interface FinanceReadinessPreviewProps {
  items: readonly FinanceReadinessPreviewItem[];
  onAddFinancialDetails: () => void;
  onOpenFinancials: () => void;
  onRefreshItem: (item: FinanceReadinessPreviewItem) => void;
  onSelectVerificationSource?: (
    source: DataSourceOption,
    item: FinanceReadinessPreviewItem,
  ) => void;
  onVerifyItem: (item: FinanceReadinessPreviewItem) => void;
}

// ── helpers ────────────────────────────────────────────────────────────────

function relativeUpdated(updatedAt: Date) {
  const ageDays = Math.max(
    0,
    Math.floor((Date.now() - updatedAt.getTime()) / (24 * 60 * 60 * 1000)),
  );
  if (ageDays <= 1) return "today";
  if (ageDays < 14) return `${ageDays}d ago`;
  const weeks = Math.floor(ageDays / 7);
  if (weeks < 9) return `${weeks}w ago`;
  return `${Math.floor(ageDays / 30)}mo ago`;
}

const isPlaidVerified = (item: FinanceReadinessPreviewItem) =>
  item.tier === "bank-verified" || item.tier === "lender-approved";

// Source letter: P = Plaid, D = Document, M = Manual/self-reported
function sourceLetter(item: FinanceReadinessPreviewItem): string {
  if (isPlaidVerified(item)) return "P";
  if (item.tier === "document-backed") return "D";
  return "M";
}

// Row background: green for Plaid-verified, muted grey for everything else
function rowToneClass(item: FinanceReadinessPreviewItem) {
  if (isPlaidVerified(item)) return "border-primary/20 bg-primary/5";
  return "border-border/40 bg-muted/40";
}

// Letter badge: green for Plaid-verified, grey/muted for everything else
function badgeToneClass(item: FinanceReadinessPreviewItem) {
  if (isPlaidVerified(item)) {
    return "border-primary/20 bg-background text-primary-dark font-bold";
  }
  return "border-border/70 bg-muted/60 text-muted-foreground font-semibold";
}

// ── component ───────────────────────────────────────────────────────────────

export function FinanceReadinessPreview({
  items,
  onAddFinancialDetails,
  onOpenFinancials,
  onRefreshItem,
  onSelectVerificationSource,
  onVerifyItem,
}: FinanceReadinessPreviewProps) {
  const [chooserItem, setChooserItem] =
    useState<FinanceReadinessPreviewItem | null>(null);

  const handleVerify = (item: FinanceReadinessPreviewItem) => {
    onVerifyItem(item);
    setChooserItem(item);
  };

  const handleSelectSource = (source: DataSourceOption) => {
    if (chooserItem) {
      onSelectVerificationSource?.(source, chooserItem);
    }
    setChooserItem(null);
  };

  if (items.length === 0) {
    return (
      <section className="surface-panel flex h-full flex-col bg-card p-5 sm:p-[22px]">
        <div className="space-y-3">
          <div className="space-y-1">
            <p className="eyebrow-label">Finance readiness</p>
            <h2 className="text-[1.06rem] font-[620] tracking-[-0.01em] text-foreground mt-1">
              What Sill knows and where it came from
            </h2>
            <p className="body-muted">
              Add your financial details so Sill can start building a clearer
              readiness picture.
            </p>
          </div>
          <Button onClick={onAddFinancialDetails}>
            Add your financial details
          </Button>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="surface-panel flex h-full flex-col bg-card p-5 sm:p-[22px]">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="eyebrow-label">Finance readiness</p>
            <h2 className="text-[1.06rem] font-[620] tracking-[-0.01em] text-foreground mt-1">
              What Sill knows and where it came from
            </h2>
          </div>
          <Button
            aria-label="View profile"
            className="shrink-0 whitespace-nowrap"
            onClick={onOpenFinancials}
            size="sm"
            variant="outline"
          >
            View profile
            <ArrowRight className="ml-1 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          </Button>
        </div>

        <div className="mt-4 space-y-1.5">
          {items.map((rawItem) => {
            // Credit has no verification path. Keep even stale or malformed
            // preview rows from offering a Verify action or a verified badge.
            const item: FinanceReadinessPreviewItem = rawItem.category === "credit-band"
              ? { ...rawItem, action: null, tier: "self-reported", verifiedAt: null }
              : rawItem;
            const secondaryLine =
              item.subcopy ??
              (item.updatedAt
                ? `Updated ${relativeUpdated(item.updatedAt)}`
                : null);

            const actionLabel =
              item.action === "verify"
                ? "Verify"
                : item.action === "refresh"
                  ? "Refresh"
                  : null;

            const handleAction = () => {
              if (item.action === "verify") handleVerify(item);
              else if (item.action === "refresh") onRefreshItem(item);
            };

            return (
              <article
                key={item.id}
                className={cn(
                  "grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-x-2.5 rounded-xl border px-2.5 py-2 transition-colors",
                  rowToneClass(item),
                )}
                data-freshness={item.freshnessState ?? null}
                data-row-id={item.id}
              >
                {/* Source letter badge */}
                <div
                  className={cn(
                    "flex size-8 shrink-0 items-center justify-center rounded-xl border text-[12px]",
                    badgeToneClass(item),
                  )}
                  aria-hidden="true"
                >
                  {sourceLetter(item)}
                </div>

                {/* Label, value on separate line, secondary line */}
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-semibold tracking-[-0.015em] text-foreground">
                    {item.label}
                  </p>
                  <p className="truncate text-[13px] font-normal text-muted-foreground">
                    {item.value}
                  </p>
                  {secondaryLine ? (
                    <p className="truncate text-[11px] leading-4 text-muted-foreground/70 mt-px">
                      {secondaryLine}
                    </p>
                  ) : null}
                </div>

                {/* Right col: pill above verify on mobile; verify left of pill on sm+ */}
                <div className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
                  {actionLabel ? (
                    <button
                      className="text-[11px] font-semibold text-primary hover:text-primary-dark transition-colors"
                      data-slot="finance-readiness-row-action"
                      onClick={handleAction}
                      type="button"
                    >
                      {actionLabel}
                    </button>
                  ) : null}
                  {!item.isInferred ? (
                    <TierBadge
                      freshnessState={item.freshnessState}
                      size="sm"
                      tier={item.tier}
                      variant="on-light"
                    />
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <Dialog
        onOpenChange={(open) => {
          if (!open) setChooserItem(null);
        }}
        open={chooserItem !== null}
      >
        <DialogContent
          description="Pick the source that should sharpen this part of your readiness picture."
          title={
            chooserItem
              ? `Verify ${chooserItem.label.toLowerCase()}`
              : "Verify your finances"
          }
        >
          {chooserItem ? (
            <div className="pt-1">
              <DataSourceChooser onSelect={handleSelectSource} />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
